import { ChangeDetectorRef, Component } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Header, TechStack } from '@aptsi-types';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { KeycloakStateService } from 'src/app/core/service/utility.service';
import { DeleteDialogComponent } from '../../shared/dialogs/delete-dialog/delete-dialog.component';
import { TechStackControlService } from '../tech-stack-control.service';

@Component({
  selector: 'app-tech-stack-control-search',
  templateUrl: './tech-stack-control-search.component.html',
  styleUrls: ['./tech-stack-control-search.component.scss']
})
export class TechStackControlSearchComponent {
// ─── Pagination ───────────────────────────────────────────────
  currentPage = 1;
  itemsPerPage = 7;
  totalPages = 0;
  totalPagesNormal = 0;
  totalPagesSearch = 0;

  // ─── User Context ─────────────────────────────────────────────
  sessionId = this.keycloakStateService.getSessionId();
  userId = this.keycloakStateService.getUserId();

  // ─── Search & Filtering ──────────────────────────────────────
  searchText: string = "";
  filterByHeader: string = "";
  inSearchMode = false;
  searchTextChanged = new Subject<string>();

  // ─── Data ─────────────────────────────────────────────────────
  techStackControls: TechStack[] = [];
  filteredData: TechStack[] = [];
  searchedData: TechStack[] = [];

  // ─── UI Configuration ─────────────────────────────────────────
  headers: Header[] = [
    {
      name: "Control Id",
      property: "id",
      showInDropdown: true,
      description: "Unique identifier for the tech stack control",
    },
    {
      name: "Tech Stack Name",
      property: "name",
      showInDropdown: true,
      description: "Name of the tech stack",
    },
    {
      name: "Tech Stack Type",
      property: "type",
      showInDropdown: true,
      description: "Type of the rule",
    }
  ];

  buttonConfig = {
    update: true,
    delete: true,
    capture: false,
  };

  constructor(
    private router: Router,
    private techStackControlService: TechStackControlService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private keycloakStateService: KeycloakStateService 
  ) {}

  // ──────────────────────────────────────────────────────────────
  // Lifecycle
  // ──────────────────────────────────────────────────────────────
  async ngOnInit(): Promise<void> {
    await this.fetchTechStackControls();

    this.searchTextChanged
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((text) => {
        this.searchText = text;
        this.searchTechStackControls();
      });

    this.cdr.detectChanges();
  }

  // ──────────────────────────────────────────────────────────────
  // Getters
  // ──────────────────────────────────────────────────────────────
  get paginatedData(): TechStack[] {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    return this.inSearchMode
      ? this.searchedData.slice(start, end)
      : this.filteredData.slice(start, end);
  }

  // ──────────────────────────────────────────────────────────────
  // Event Handlers
  // ──────────────────────────────────────────────────────────────
  changePage(page: number): void {
    this.currentPage = page;
    this.totalPages = this.inSearchMode
      ? this.totalPagesSearch
      : this.totalPagesNormal;
    this.inSearchMode ? this.fetchTechStackControlsSearch() : this.fetchTechStackControls();
  }

  onSearchTextChange(searchValue: string): void {
    this.searchTextChanged.next(searchValue);
  }

  handleAction(event: {
    action: string;
    rowIndex: number;
    rowData: TechStack;
  }): void {
    if (event.action === "update") {
      this.updateTechStack(event.rowIndex);
    } else if (event.action === "delete") {
      this.deleteTechStack(event.rowIndex);
    }
  }

  // ──────────────────────────────────────────────────────────────
  // Navigation
  // ──────────────────────────────────────────────────────────────
  updateTechStack(index: number): void {
    const techStackControlId = this.inSearchMode
      ? this.searchedData[index].id
      : this.filteredData[index].id;
    this.router.navigate([
      "insight-egrc/auth-tools/tech-stack-control/update",
      techStackControlId,
    ]);
  }

  createTechStackControl(): void {
    this.router.navigate([
      "insight-egrc/auth-tools/tech-stack-control/create",
    ]);
  }

  back(): void {
    if (this.inSearchMode) {
      this.inSearchMode = false;
      this.fetchTechStackControls();
    } else {
      this.router.navigate(["insight-egrc/auth-tools"]);
    }
  }

  // ──────────────────────────────────────────────────────────────
  // Data Fetching
  // ──────────────────────────────────────────────────────────────
  async fetchTechStackControls(): Promise<void> {
    try {
      const response = await this.techStackControlService
        .getTechStackControls(this.currentPage - 1, this.sessionId, this.userId)
        .toPromise();
      this.formatAndSetRules(response["Resource retrieved successfully"], false);
    } catch (error) {
      console.error("Error fetching tech stack controls:", error);
    }
  }

  async fetchTechStackControlsSearch(): Promise<void> {
    try {
      const response = await this.techStackControlService
        .getTechStackControlSearch(
          this.currentPage - 1,
          this.searchText,
          this.filterByHeader
        )
        .toPromise();
      this.formatAndSetRules(response, true);
    } catch (error) {
      console.error("Error fetching rule search:", error);
    }
  }

  private formatAndSetRules(response: any[], inSearchMode: boolean): void {
    const formatted = response.map((techStackControl) => ({
      ...techStackControl
    }));

    if (inSearchMode) {
      this.searchedData = formatted;
      this.totalPagesSearch = response[0]?.totalPages || 0;
    } else {
      this.filteredData = formatted;
      this.totalPagesNormal = response[0]?.totalPages || 0;
    }

    this.updatePagination(response);
  }

  private updatePagination(response: any[]): void {
    if (response.length > 0 && response[0].totalPages) {
      const totalPages = response[0].totalPages;
      const currentPage = response[0].currentPage + 1;

      if (this.inSearchMode) {
        this.totalPagesSearch = totalPages;
      } else {
        this.totalPagesNormal = totalPages;
      }

      this.totalPages = totalPages;
      this.currentPage = currentPage;
    }
  }

  // ──────────────────────────────────────────────────────────────
  // Search
  // ──────────────────────────────────────────────────────────────
  async searchTechStackControls(): Promise<void> {
    try {
      if (this.searchText) {
        this.inSearchMode = true;

        await this.fetchTechStackControlsSearch();
        this.totalPages = this.totalPagesSearch;

        const selectedHeader = this.headers.find(
          (h) => h.property === this.filterByHeader && h.showInDropdown
        );
        if (selectedHeader) {
          const key = selectedHeader.property as keyof TechStack;
          this.searchedData = this.searchedData.filter((techStack) => {
            const value = techStack[key];
            return value
              ?.toString()
              .toLowerCase()
              .includes(this.searchText.toLowerCase());
          });

          this.searchedData.sort((a, b) =>
            a[key]
              ?.toString()
              .localeCompare(b[key]?.toString())
          );
        } else {
          this.filteredData = [];
        }
      } else {
        this.inSearchMode = false;

        await this.fetchTechStackControls();
      }
    } catch (error) {
      console.error("Error during search:", error);
    }
  }

  

  // ──────────────────────────────────────────────────────────────
  // Delete
  // ──────────────────────────────────────────────────────────────
  async deleteTechStack(index: number): Promise<void> {
    try {
      const dialogRef = this.dialog.open(DeleteDialogComponent, {
        data: {
          title: "Are you sure?",
          message: "Do you really want to delete this Rule?",
          isConfirm: false,
        },
      });

      const result = await dialogRef.afterClosed().toPromise();
      if (!result) return;

      const techStackControlId = this.inSearchMode
        ? this.searchedData[index].id
        : this.filteredData[index].id;
      await this.techStackControlService.deleteTechStackControl(techStackControlId).toPromise();

      this.inSearchMode ? this.fetchTechStackControlsSearch() : this.fetchTechStackControls();

      this.dialog.open(DeleteDialogComponent, {
        data: {
          title: "Success",
          message: "The Tech Stack Control has been deleted successfully.",
          isConfirm: true,
        },
      });
    } catch (error) {
      console.error("Error deleting Tech Stack Control:", error);
    }
  }
}
